<template>
  <DataTable
    :loading="loading"
    :rows="rows"
    :cols-map="visibleColsMap"
    :nested-cols-map="visibleColsMap"
    nested-key="children"
    sortable
    paginated
    searchable
    :page="currentPage"
    :total="total"
    :limit="limit"
    :error-message="errorMessage"
    :cell-checks="!readonly"
    @update:page="updateOffset"
    @update:page-limit="updatePageLimit"
    @cell-create="(colKey: string | number, row: any) => updateDetail('create', colKey, row)"
    @cell-delete="(colKey: string | number, row: any) => updateDetail('delete', colKey, row)"
    @search="
      (value: string) => {
        search = value;
        updateOffset(0);
      }
    "
  >
    <template #toolbar-actions>
      <AdminColumnSelect
        v-if="colsKeys.length > 5"
        v-model:visible-cols="visibleColsKeys"
        class="ms-3"
        :cols="colsKeys"
      />
    </template>
    <template #customCells="{row, cols}">
      <template v-for="(col, _index) in cols" :key="_index">
        <td v-if="col === 'name'">
          <span
            :class="['text-nowrap', {'fw-bold': row['level'] < 2}]"
            :style="`margin-left: ${row['level']}rem;`"
          >
            {{ row[col] }}
          </span>
        </td>
        <DataTableCell
          v-else
          :row="row"
          :col="col"
          :cols-values="cols"
          :loading-col="loadingCol"
          :loading-row="loadingRow"
          cell-checks
          @update="
            (colKey: string | number, row: any, value: boolean) =>
              updateDetail(value ? 'create' : 'delete', colKey, row)
          "
        />
      </template>
    </template>
  </DataTable>
</template>
<script lang="ts" setup>
const props = defineProps<{content: any}>();

const name = computed(() => props.content.name);
const kwargs = computed(() => props.content.kwargs);
const queryChar = kwargs.value?.url?.includes('?') ? '&' : '?';

// Stores
const adminStore = useAdminStore();
const admin = computed(() => adminStore.admin[name.value] || null);

const loading = ref(true);
const errorMessage = ref('');
const loadingCol = ref<string | undefined>();
const loadingRow = ref<string | undefined>();

const readonly = computed(() => admin.value?.results?._meta?.readonly);

const rows = computed(() => admin.value?.results?.items || []);
const colsMap = computed(() => admin.value?.results?.column_map || {});
const colsKeys = computed(() => Object.keys(colsMap.value) || []);
const visibleColsKeys = ref<string[]>(Object.keys(colsMap.value) || []);
const visibleColsMap = computed(() => {
  return Object.keys(colsMap.value)
    .filter((key) => visibleColsKeys.value.includes(key))
    .reduce((obj: {[key: string]: any}, key) => {
      obj[key] = colsMap.value[key];
      return obj;
    }, {});
});

const total = computed(() => admin.value?.total);
const limit = computed(() => admin.value?.limit);

const pageLimit = ref(100);
const currentOffset = ref(0);

const calculatedPage = computed(() =>
  admin.value !== null ? Math.ceil((admin.value.offset - 1) / admin.value.limit) + 1 : 1,
);
const currentPage = ref(calculatedPage.value);

const fetchData = async (url: string) => {
  loading.value = true;
  try {
    await adminStore.readAdminEndpoint(name.value, url);
    visibleColsKeys.value = colsKeys.value;
    loading.value = false;
  } catch (error: any) {
    errorMessage.value = error;
  }
};

const search = ref('');

const updateOffset = async (offset: number) => {
  loading.value = true;
  try {
    await fetchData(
      `${kwargs.value.url}${queryChar}limit=${pageLimit.value}&offset=${offset}&search=${search.value}`,
    );
    currentPage.value = offset;
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    loading.value = false;
  }
};

const updatePageLimit = (newLimit: number) => {
  pageLimit.value = newLimit;
  let offset = (currentPage.value - 1) * pageLimit.value;
  updateOffset(offset);
};

const emptyLoading = () => {
  loadingCol.value = undefined;
  loadingRow.value = undefined;
};

const updateDetail = async (action: string, columnId: string | number, row: any) => {
  const type = row.type;
  loadingCol.value = String(columnId);
  loadingRow.value = row.id;
  try {
    if (action === 'create')
      await adminStore.createMatrixObject(name.value, String(columnId), row.id, type);
    if (action === 'delete')
      await adminStore.removeMatrixObject(name.value, String(columnId), row.id, type);
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    emptyLoading();
  }
};

// Fine for the purposes of rendering this one table
watch(kwargs, (newValue, oldValue) => {
  if (newValue.url !== oldValue.url) {
    currentPage.value = 0;
    updateOffset(0);
  }
});

onMounted(async () => {
  if (!kwargs.value?.url) {
    errorMessage.value = 'This page is missing kwargs content';
    loading.value = false;
  } else {
    await fetchData(
      `${kwargs.value.url}${queryChar}limit=${pageLimit.value}&offset=${currentOffset.value}`,
    );
  }
  visibleColsKeys.value = colsKeys.value;
});
</script>
